import React, { useState, useEffect } from 'react';

import i18n from 'i18n-js';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Formik, Field, useFormikContext } from 'formik';
import * as FormikFields from 'formik-material-ui';
import {
    Avatar,
    Checkbox,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField as MuiTextField,
    FormControlLabel,
    TableHead,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import DeleteIcon from '@material-ui/icons/Delete';
import * as yup from 'yup';
import { parsePhoneNumber } from 'react-phone-number-input';
import { Switch } from 'formik-material-ui';

import { UserData } from 'store/usersStore/reducer';
import { ApplicationState } from 'store';
import { default as restaurantStore } from 'store/restaurantDetailsStore';
import { regexHelper } from 'helpers/regexHelper';
import agent from 'api/agent';

import {
    CustomPhoneInput,
    Input,
    PopupConfirmation,
    SectionTab,
    Text,
    UserRoleLabel,
    ImageContainer,
} from 'components/shared';
import globalStore from 'store/globalStore';
import AddNewUserModal from '../components/AddNewUserModal';
import { useStyles } from '../styles';
import { RestaurantCreateData } from '..';
import { RestaurantDetails } from 'store/restaurantDetailsStore/reducer';
import { IdName } from 'components/restaurants/restaurantsPage/components/NewRestaurantDialog';
import { getCoordinatesFromTheAddress } from 'helpers/helperFunctions';

interface InnerFormProps {
    onMountSubmit: (submitFn: () => void) => void;
    onStatusChange: (status: { dirty: boolean; isValid: boolean; isSubmitting: boolean }) => void;
}

interface RestaurantDetailsFormProps extends InnerFormProps {
    restaurant: RestaurantDetails;
    onSubmit: (values: RestaurantCreateData) => void;
    contactPersonsEnabled?: boolean;
}

export default function RestaurantDetailsForm(props: RestaurantDetailsFormProps): JSX.Element {
    const classes = useStyles();
    const dispatch = useDispatch();

    const cuisineTypes = useSelector(
        (state: ApplicationState) => state.restaurantDetails.cuisineTypes
    );
    const browserData = useSelector((state: ApplicationState) => state.user.browserData);

    const [selectedContactPerson, setSelectedContactPerson] = useState<UserData | string>('');
    const [contactPersons, setContactPersons] = useState<UserData[]>([]);
    const [restaurants, setRestaurants] = useState<IdName[]>([]);
    const [logoHasChanged, setLogoHasChanged] = useState(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [showUsersTable, setShowUsersTable] = useState(false);

    const fileUploadRef: any = React.useRef(null);

    function prepareFormValues(): RestaurantCreateData {
        return {
            name: props.restaurant?.name || '',
            addressLine: props.restaurant?.addressLine || '',
            zip: props.restaurant?.zip || '',
            city: props.restaurant?.city || '',
            phoneNumber: props.restaurant?.phoneNumber || '',
            email: props.restaurant?.email || '',
            logoUrl: props.restaurant?.logoUrl || '',
            website: props.restaurant?.website || '',
            latitude: props.restaurant?.latitude || 0,
            longitude: props.restaurant?.longitude || 0,
            type: props.restaurant?.type || [],
            percentageForDineIn: props.restaurant?.percentageForDineIn || 0,
            percentageForTakeAway: props.restaurant.percentageForTakeAway || 0,
            percentageForHomeDelivery: props.restaurant.percentageForHomeDelivery || 0,
            timeZone: props.restaurant?.timeZone || '',
            isCoolBox: props.restaurant.coolBoxData?.isCoolBox || false,
            isPublic: props.restaurant.coolBoxData?.isPublic || false,
            coolBoxName: props.restaurant.coolBoxData?.coolBoxName || '',
            isDeliverAnytime: props.restaurant.isDeliverAnytime || false,
        };
    }

    const validationSchema = yup.object().shape({
        name: yup
            .string()
            .max(
                128,
                i18n.t('form.errors.maxLength', {
                    name: 'Name',
                    length: 128,
                })
            )
            .required(
                i18n.t('form.errors.required', {
                    name: 'Name',
                })
            )
            .test('existsCheck', i18n.t('form.errors.alreadyTaken'), (value: any) => {
                return restaurants.every((r) => r.name !== value.trim());
            }),
        addressLine: yup.string().required(
            i18n.t('form.errors.required', {
                name: 'Address line',
            })
        ),
        email: yup
            .string()
            .required(
                i18n.t('form.errors.required', {
                    name: 'Email',
                })
            )
            .email(i18n.t('form.errors.email')),
        phoneNumber: yup
            .string()
            .required(
                i18n.t('form.errors.required', {
                    name: 'Phone number',
                })
            )
            .matches(regexHelper.phoneNumber, i18n.t('form.errors.phoneNumber')),
        zip: yup.string().max(10),
        city: yup.string().matches(regexHelper.city, i18n.t('form.errors.city')),
        coolBoxName: yup.string().when('isCoolBox', {
            is: true,
            then: yup.string().required(
                i18n.t('form.errors.required', {
                    name: 'CoolBox Name',
                })
            ),
        }),
    });
    const [showAddContactModal, setShowAddContactModal] = useState(false);

    /* function createContactPerson(model: RestaurantUserCreateModel) {
        dispatch(globalStore.actionCreators.showSpiner());
        agent.Users.CreateRestaurantOwner(model)
            .then((response) => setContactPersons([...contactPersons, response]))
            .catch((e) => dispatch(globalStore.actionCreators.showToaster('error', e)))
            .finally(() => dispatch(globalStore.actionCreators.hideSpiner()));
    }

    const createContactPersons = (model: RestaurantContactPerson[], id: string, name: string) => {
        return new Promise((resolve, reject) => {
            const persons = model.map(
                (p) =>
                    ({
                        ...p,
                        restaurantId: id,
                        isContactPerson: true,
                        restaurantName: name,
                    } as RestaurantUserCreateModel)
            );
            try {
                persons.forEach((x) => {
                    agent.Users.CreateRestaurantOwner(x);
                });
                return resolve('Created succesfully!');
            } catch (ex) {
                return reject(ex);
            }
        });
    }; */
    const removeContactPerson = (user: UserData) => console.log(user);
    /* const removeContactPerson = (user: UserData) => {
        dispatch(globalStore.actionCreators.showSpiner());
        agent.Users.EditRestaurantUser({
            restaurant: {
                id: params.id,
                name: formValues.name,
                addressLine: formValues.addressLine,
                zip: formValues.zip,
                city: formValues.city,
                logoUrl: formValues.logoUrl ?? '',
            },
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            phoneNumber: user.phoneNumber,
            isContactPerson: false,
            positions: user.restaurants.find((x) => x.restaurantId === params.id)!.positions,
            isOwner: user.roles[0].name === Roles.OWNER,
        })
            .then((response) => {
                setContactPersons(contactPersons.filter((x) => x.id !== response.id));
            })
            .catch((e) => dispatch(globalStore.actionCreators.showToaster('error', e)))
            .finally(() => dispatch(globalStore.actionCreators.hideSpiner()));
    }; */

    /* async function lastSubmit(values: RestaurantCreateData) {
        dispatch(globalStore.actionCreators.showSpiner());
        if (
            !params.id ||
            values.latitude === 0 ||
            values.longitude === 0 ||
            values.addressLine !== previuosState.addressLine ||
            values.city !== previuosState.city ||
            values.zip !== previuosState.zip
        ) {
            const { latitude, longitude, timeZone } = await getCoordinatesFromTheAddress(
                `${values.addressLine}, ${values.zip} ${values.city}`
            );
            values.latitude = latitude;
            values.longitude = longitude;
            values.timeZone = timeZone;
        }
        if (values.logoUrl && logoHasChanged) {
            values.logoUrl = await fileUploadRef.current?.uploadImage();
        }

        const cuisineTypes: string[] = [];
        values.type.map((type) => cuisineTypes.push(type.id));
        const newValues = { ...values };
        newValues.type = cuisineTypes;
        if (params.id) {
            agent.Restaurants.Update(params.id, newValues)
                .then((response) => {
                    if (contactPersons.length > previuosContactPersons.length) {
                        let newContactPersons = [...contactPersons];
                        newContactPersons.splice(0, previuosContactPersons.length);
                        return createContactPersons(newContactPersons, response.id, response.name);
                    }
                    setLogoHasChanged(false);
                    setFormChanged(false);
                    updatePreviousState(response);
                    dispatch(
                        globalStore.actionCreators.showToaster(
                            'success',
                            i18n.t('messages.updatedSuccesfully')
                        )
                    );
                    // getRestaurantDetails();
                })
                .catch((e) => {
                    dispatch(globalStore.actionCreators.showToaster('error', e));
                })
                .finally(() => dispatch(globalStore.actionCreators.hideSpiner()));
        } else {
            agent.Restaurants.Create(newValues)
                .then((response) => {
                    history.push(`/restaurants/${response.id}`);
                })
                .catch((e) => {
                    dispatch(globalStore.actionCreators.showToaster('error', e));
                })
                .finally(() => dispatch(globalStore.actionCreators.hideSpiner()));
        }
    } */

    useEffect(() => {
        agent.Restaurants.GetSimpleList()
            .then((response) => setRestaurants(response))
            .catch((e) => {
                globalStore.actionCreators.showToaster('error', e);
            });
    }, []);

    useEffect(() => {
        if (!cuisineTypes.length) {
            dispatch(restaurantStore.actionCreators.getCuisineTypes());
        }

        setContactPersons(props.restaurant.contactPersons);
    }, [props.restaurant.id]);

    return (
        <>
            <Formik
                enableReinitialize={true}
                initialValues={prepareFormValues()}
                validationSchema={validationSchema}
                validateOnMount
                onSubmit={async (values, { setFieldValue }) => {
                    const parsedPhoneNumber = parsePhoneNumber(values.phoneNumber);
                    if (parsedPhoneNumber) {
                        if (!parsedPhoneNumber.country) {
                            const fixedPhoneNumber = values.phoneNumber.replace(
                                '+' + parsedPhoneNumber.countryCallingCode.toString(),
                                browserData?.country_calling_code
                            );
                            setFieldValue('phoneNumber', fixedPhoneNumber);
                            values.phoneNumber = fixedPhoneNumber;
                        }
                    }
                    if (
                        !props.restaurant.id ||
                        values.latitude === 0 ||
                        values.longitude === 0 ||
                        values.addressLine !== props.restaurant.addressLine ||
                        values.city !== props.restaurant.city ||
                        values.zip !== props.restaurant.zip
                    ) {
                        const { latitude, longitude, timeZone } =
                            await getCoordinatesFromTheAddress(
                                `${values.addressLine}, ${values.zip} ${values.city}`
                            );
                        values.latitude = latitude;
                        values.longitude = longitude;
                        values.timeZone = timeZone;
                    }
                    if (values.logoUrl && logoHasChanged) {
                        values.logoUrl = await fileUploadRef.current?.uploadImage();
                    }

                    const cuisineTypes: string[] = [];
                    values.type.map((type) => cuisineTypes.push(type.id));
                    values.type = cuisineTypes;

                    props.onSubmit(values);
                    dispatch(globalStore.actionCreators.hideSpiner());
                }}
            >
                <InnerForm {...props} />
            </Formik>
            <TableContainer style={{ overflowX: 'auto', marginTop: 0 }}>
                <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={6} padding="none">
                                <SectionTab
                                    disabled={!props.restaurant.id || !props.contactPersonsEnabled}
                                    text={i18n.t('restaurant.addContactPerson')}
                                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                        /* if (
                                                            !!!params.id &&
                                                            contactPersons.length < 2
                                                        ) {
                                                            setShowAddContactModal(true);
                                                        } else {
                                                            setAnchorEl(event.currentTarget);
                                                        } */
                                        setAnchorEl(event.currentTarget);
                                    }}
                                />
                                <AddNewUserModal
                                    anchorEl={anchorEl}
                                    handleClose={() => setAnchorEl(null)}
                                    openNew={() => setShowAddContactModal(true)}
                                    openExists={() => setShowUsersTable(true)}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {contactPersons.map((person: UserData) => (
                            <TableRow key={person.id}>
                                <TableCell scope="row">
                                    <List>
                                        <ListItem alignItems="center">
                                            <ListItemAvatar>
                                                <Avatar
                                                    alt="Remy Sharp"
                                                    src="/static/images/avatar/1.jpg"
                                                />
                                            </ListItemAvatar>
                                            <Text
                                                text={`${person.firstName} ${person.lastName}`}
                                                maxLength={20}
                                            />
                                        </ListItem>
                                    </List>
                                </TableCell>
                                <TableCell align="left">
                                    <Text text={person.email} fontSize={14} maxLength={30} />
                                </TableCell>
                                <TableCell align="left">{person.phoneNumber}</TableCell>
                                <TableCell align="left">
                                    {person.restaurants
                                        .find((x) => x.restaurantId === props.restaurant.id)
                                        ?.positions.join(', ') || '-'}
                                </TableCell>
                                <TableCell align="left" style={{ width: 140 }}>
                                    <UserRoleLabel role={person.roles[0].name} />
                                </TableCell>
                                <TableCell align="right">
                                    {props.contactPersonsEnabled && (
                                        <IconButton
                                            aria-label="more"
                                            aria-controls="long-menu"
                                            aria-haspopup="true"
                                            onClick={() => {
                                                setSelectedContactPerson(person);
                                            }}
                                        >
                                            <DeleteIcon color={'primary'} />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}

                        <PopupConfirmation
                            open={Object.keys(selectedContactPerson).length > 0}
                            close={() => setSelectedContactPerson('')}
                            title={i18n.t('common.deleteUser')}
                            description={i18n.t('confirmation.deleteUser')}
                            activeBtn={i18n.t('common.delete')}
                            action={() =>
                                typeof selectedContactPerson === 'object' &&
                                removeContactPerson(selectedContactPerson)
                            }
                        />
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );

    function InnerForm(props: InnerFormProps): JSX.Element {
        const { submitForm, setFieldValue, dirty, isValid, isSubmitting, values, errors } =
            useFormikContext<RestaurantCreateData>();

        useEffect(() => {
            props.onMountSubmit(submitForm);
        }, [submitForm]);

        useEffect(() => {
            props.onStatusChange({ dirty, isValid, isSubmitting });
        }, [dirty, isValid, isSubmitting]);

        return (
            <Form className={classes.formInnerContainer}>
                <div className={classes.leftContainer}>
                    <h6 className={classes.sectionTitle}>
                        {i18n.t('restaurant.restaurantInformation').toUpperCase()}
                    </h6>
                    <div className={classes.flexRowSpaceBetween}>
                        <div style={{ width: '49%' }}>
                            {' '}
                            <Input
                                id="name"
                                name="name"
                                type="text"
                                error={errors.name}
                                variant="filled"
                                placeholder={i18n.t('form.restaurantName')}
                                label={i18n.t('form.restaurantName')}
                                className={classes.inputContainer}
                            />
                            <CustomPhoneInput
                                containerStyle={{
                                    margin: '0',
                                    height: '35px',
                                    marginBottom: 30,
                                }}
                                invalid={errors.phoneNumber}
                                placeholder={i18n.t('form.phoneNumber')}
                                value={values.phoneNumber}
                                onChange={(val: any) => setFieldValue('phoneNumber', val)}
                            />{' '}
                            <Input
                                id="email"
                                placeholder={i18n.t('form.email')}
                                name="email"
                                error={errors.email}
                                type="text"
                                label={i18n.t('form.email')}
                                variant="filled"
                                className={classes.inputContainer}
                            />
                        </div>
                        <div style={{ width: '49%' }}>
                            <Input
                                id="addressLine"
                                placeholder={i18n.t('form.restaurantAddress')}
                                name="addressLine"
                                error={errors.addressLine}
                                type="text"
                                label={i18n.t('form.restaurantAddress')}
                                variant="filled"
                                className={classes.inputContainer}
                            />
                            <div className={classes.halfFormInputContainer}>
                                <Input
                                    id="zip"
                                    className={classes.halfFormInput}
                                    placeholder={i18n.t('form.zip')}
                                    name="zip"
                                    error={errors.zip}
                                    type="text"
                                    label={i18n.t('form.zip')}
                                    variant="filled"
                                />
                                <Input
                                    id="city"
                                    className={classes.halfFormInput}
                                    placeholder={i18n.t('form.city')}
                                    name="city"
                                    error={errors.city}
                                    type="text"
                                    label={i18n.t('form.city')}
                                    variant="filled"
                                />
                            </div>
                            <Input
                                id="website"
                                placeholder={i18n.t('form.webpage')}
                                name="website"
                                error={errors.website}
                                type="text"
                                label={i18n.t('form.webpage')}
                                variant="filled"
                                className={classes.inputContainer}
                            />
                        </div>
                    </div>
                    <h6 className={classes.sectionTitle} style={{ marginTop: 0 }}>
                        {i18n.t('restaurant.percentageFor').toUpperCase()}
                    </h6>
                    <Input
                        placeholder={i18n.t('form.dineIn')}
                        name="percentageForDineIn"
                        error={errors.percentageForDineIn}
                        type="number"
                        label={i18n.t('form.dineIn')}
                        variant="filled"
                        className={classes.percentageInput}
                        maxLength={2}
                        inputProps={{
                            min: 0,
                            max: 99,
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment
                                    position="start"
                                    className={classes.percentageString}
                                    style={{
                                        display: values.percentageForDineIn?.toString().length
                                            ? 'flex'
                                            : 'none',
                                    }}
                                >
                                    %
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Input
                        placeholder={i18n.t('form.takeAway')}
                        name="percentageForTakeAway"
                        error={errors.percentageForTakeAway}
                        type="number"
                        label={i18n.t('form.takeAway')}
                        variant="filled"
                        className={classes.percentageInput}
                        maxLength={2}
                        inputProps={{
                            min: 0,
                            max: 99,
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment
                                    position="start"
                                    className={classes.percentageString}
                                    style={{
                                        display: values.percentageForTakeAway.toString().length
                                            ? 'flex'
                                            : 'none',
                                    }}
                                >
                                    %
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Input
                        placeholder={i18n.t('form.homeDelivery')}
                        name="percentageForHomeDelivery"
                        error={errors.percentageForHomeDelivery}
                        type="number"
                        label={i18n.t('form.homeDelivery')}
                        variant="filled"
                        className={classes.percentageInput}
                        maxLength={2}
                        inputProps={{
                            min: 0,
                            max: 99,
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment
                                    position="start"
                                    className={classes.percentageString}
                                    style={{
                                        display: values.percentageForHomeDelivery.toString().length
                                            ? 'flex'
                                            : 'none',
                                    }}
                                >
                                    %
                                </InputAdornment>
                            ),
                        }}
                    />
                    <h6 className={classes.sectionTitle}>
                        {i18n.t('restaurant.contactPerson').toUpperCase()}
                    </h6>
                </div>
                <div className={classes.rightContainer}>
                    <div className={classes.uploadLogoContainer}>
                        <ImageContainer
                            ref={fileUploadRef}
                            photo={values.logoUrl}
                            onChange={(_, fileUrl) => {
                                setFieldValue('logoUrl', fileUrl);
                                setLogoHasChanged(true);
                                //triggerFormChange();
                            }}
                            styles={{ height: '100%', width: '100%' }}
                        />
                    </div>
                    <h6 className={classes.sectionTitle} style={{ marginTop: 5, marginBottom: 15 }}>
                        {i18n.t('restaurant.typeOfCuisine').toUpperCase()}
                    </h6>
                    <Autocomplete
                        options={cuisineTypes}
                        getOptionLabel={(option) => option.name}
                        getOptionSelected={(option, value) => option.id === value.id}
                        classes={{
                            option: classes.dropdownItem,
                            listbox: classes.listBox,
                            paper: classes.paper,
                        }}
                        renderOption={(option) => {
                            const exists = !!values.type.find(
                                (currentOption: any) => currentOption.id === option.id
                            );
                            return (
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        let newValues;
                                        if (exists) {
                                            newValues = values.type.filter(
                                                (selectedOption: any) =>
                                                    selectedOption.id !== option.id
                                            );
                                        } else {
                                            newValues = [...values.type, option];
                                        }
                                        setFieldValue('type', newValues);
                                        //triggerFormChange();
                                    }}
                                    className={`${classes.width100} ${classes.bottomLine} ${
                                        exists ? classes.selectedDropdownItem : ''
                                    }`}
                                >
                                    <Checkbox color="primary" checked={exists} />
                                    {option.name}
                                </div>
                            );
                        }}
                        renderInput={(params) => (
                            <>
                                <MuiTextField
                                    {...params}
                                    name="type"
                                    label={
                                        values.type.length === 0
                                            ? i18n.t('common.chooseTheType')
                                            : `${values.type.length} ${i18n.t(
                                                  'restaurant.cuisines'
                                              )}`
                                    }
                                    variant="filled"
                                    fullWidth
                                />
                            </>
                        )}
                    />
                    <Input
                        component={FormikFields.CheckboxWithLabel}
                        variant="filled"
                        checked={values.isDeliverAnytime}
                        name={'isDeliverAnytime'}
                        key="isDeliverAnytime"
                        type="checkbox"
                        Label={{
                            label: i18n.t('restaurant.deliverAnytime'),
                        }}
                        placeholder={i18n.t('restaurant.deliverAnytime')}
                        color="primary"
                    />
                    <h6
                        className={classes.sectionTitle}
                        style={{ marginTop: 10, marginBottom: 15 }}
                    >
                        {i18n.t('restaurant.jamesCoolBox').toUpperCase()}
                    </h6>
                    <div className={classes.coolBoxContainer}>
                        <Input
                            component={FormikFields.CheckboxWithLabel}
                            variant="filled"
                            name="isCoolBox"
                            type="checkbox"
                            Label={{
                                label: i18n.t('restaurant.jamesCoolBox'),
                            }}
                            placeholder={i18n.t('restaurant.jamesCoolBox')}
                            color="primary"
                        />
                        <br />
                        <FormControlLabel
                            aria-label="Acknowledge"
                            style={{ fontSize: 14 }}
                            control={
                                <Field
                                    component={Switch}
                                    name="isPublic"
                                    color="primary"
                                    type="checkbox"
                                    disabled={!values.isCoolBox}
                                    style={{ fontSize: 14 }}
                                />
                            }
                            label={i18n.t('restaurant.publicCoolBox')}
                        />
                        <br />
                        <Input
                            id="coolbox-name"
                            placeholder={i18n.t('restaurant.coolBoxName')}
                            name="coolBoxName"
                            error={errors.coolBoxName}
                            type="text"
                            label={i18n.t('restaurant.coolBoxName')}
                            variant="filled"
                            className={classes.inputContainer}
                            disabled={!values.isCoolBox}
                            style={{ marginTop: 10 }}
                        />
                    </div>
                </div>
            </Form>
        );
    }
}
